'use client'

import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { SendIcon, RefreshCw } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { PhoneInput } from '@/components/ui/phone-input'
import { EMAIL_REGEX } from '@/constants'
import { useCaptcha } from '@/hooks'
import { submitContactForm } from '@/lib/api'
import { getErrorMessage, setValidationErrors } from '@/lib/utils'

type PropsType = {
  captcha?: boolean
}

const ContactForm = ({ captcha }: PropsType) => {
  const t = useTranslations()

  const {
    data: captchaData,
    isLoading: isLoadingCaptcha,
    refetch: refreshCaptcha
  } = useCaptcha({ size: 'medium' }, !!captcha)

  const formSchema = z.object({
    firstname: z
      .string()
      .trim()
      .min(1, t('required'))
      .max(100, t('max_value', { value: 100 })),
    lastname: z
      .string()
      .trim()
      .min(1, t('required'))
      .max(100, t('max_value', { value: 100 })),
    email: z
      .string()
      .trim()
      .min(1, t('required'))
      .refine(val => EMAIL_REGEX.test(val), t('invalid_email')),
    phone: z
      .string()
      .trim()
      .max(50, t('max_value', { value: 50 }))
      .optional(),
    subject: z
      .string()
      .trim()
      .min(1, t('required'))
      .max(255, t('max_value', { value: 255 })),
    message: z
      .string()
      .trim()
      .min(1, t('required'))
      .max(5000, t('max_value', { value: 5000 })),
    captchaText: captcha ? z.string().trim().min(1, t('required')) : z.string().optional()
  })

  type FormValues = z.infer<typeof formSchema>

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstname: '',
      lastname: '',
      email: '',
      phone: '',
      subject: '',
      message: '',
      captchaText: ''
    }
  })

  const { mutate, isPending } = useMutation({
    mutationFn: (data: FormValues) =>
      submitContactForm({
        firstname: data.firstname,
        lastname: data.lastname,
        email: data.email,
        phone: data.phone || undefined,
        subject: data.subject,
        message: data.message,
        ...(captcha && captchaData ? { captcha: { key: captchaData.key, text: data.captchaText ?? '' } } : {})
      }),
    onSuccess: async () => {
      toast.success(t('message_sent_success'))
      form.reset()
      if (captcha) {
        await refreshCaptcha()
      }
    },
    onError: async (error: any) => {
      if (error.response?.status === 422) {
        const items = error?.response?.data?.items || {}
        const remapped = Object.fromEntries(
          Object.entries(items).map(([key, val]) => [key === 'captcha' ? 'captchaText' : key, val])
        )
        setValidationErrors({ response: { data: { items: remapped } } }, form.setError)
      } else {
        toast.error(getErrorMessage(error, t('message_sent_error')))
      }
      if (captcha) {
        await refreshCaptcha()
      }
    }
  })

  const onSubmit = (data: FormValues) => mutate(data)

  return (
    <div className="bg-card rounded-lg border p-8 shadow-xs md:p-10">
      <div className="mb-8 text-center">
        <h2 className="mb-3 text-3xl font-bold">{t('send_us_message')}</h2>
        <p className="text-muted-foreground">{t('contact_description')}</p>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid items-start gap-6 md:grid-cols-2 lg:grid-cols-4">
            <FormField
              control={form.control}
              name="firstname"
              render={({ field }) => (
                <FormItem>
                  <FormLabel required>{t('firstname')}</FormLabel>
                  <FormControl>
                    <Input {...field} disabled={isPending} placeholder={t('firstname')} className="h-11" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="lastname"
              render={({ field }) => (
                <FormItem>
                  <FormLabel required>{t('lastname')}</FormLabel>
                  <FormControl>
                    <Input {...field} disabled={isPending} placeholder={t('lastname')} className="h-11" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel required>{t('your_email')}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="email"
                      disabled={isPending}
                      placeholder={t('your_email')}
                      className="h-11"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('your_phone')}</FormLabel>
                  <FormControl>
                    <PhoneInput
                      international
                      value={field.value}
                      onChange={field.onChange}
                      disabled={isPending}
                      className="[&_input]:h-11 [&>button]:h-11"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="subject"
            render={({ field }) => (
              <FormItem>
                <FormLabel required>{t('subject')}</FormLabel>
                <FormControl>
                  <Input {...field} disabled={isPending} placeholder={t('subject')} className="h-11" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="message"
            render={({ field }) => (
              <FormItem>
                <FormLabel required>{t('message')}</FormLabel>
                <FormControl>
                  <Textarea {...field} disabled={isPending} placeholder={t('message')} className="h-32" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid items-end justify-between gap-6 md:grid-cols-2">
            <div>
              {captcha && captchaData && (
                <FormField
                  control={form.control}
                  name="captchaText"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-4">
                        <div className="flex items-center gap-1 sm:mt-5">
                          <div className="bg-muted relative overflow-hidden rounded-lg border">
                            {isLoadingCaptcha ? (
                              <div className="flex h-15 w-45 items-center justify-center">
                                <RefreshCw className="text-muted-foreground h-6 w-6 animate-spin" />
                              </div>
                            ) : (
                              <Image
                                src={captchaData.image}
                                alt="Captcha"
                                width={180}
                                height={60}
                                className="h-auto w-auto"
                                unoptimized
                              />
                            )}
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            size="icon-lg"
                            tabIndex={-1}
                            onClick={async () => {
                              field.onChange('')
                              await refreshCaptcha()
                            }}
                            disabled={isPending || isLoadingCaptcha}
                            className="h-11.5 w-11.5 shrink-0">
                            <RefreshCw />
                          </Button>
                        </div>
                        <div className="w-full space-y-2 sm:w-48">
                          <FormLabel required>{t('security_code')}</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              disabled={isPending}
                              placeholder={t('enter_security_code')}
                              autoComplete="off"
                              className="h-11"
                            />
                          </FormControl>
                          <FormMessage />
                        </div>
                      </div>
                    </FormItem>
                  )}
                />
              )}
            </div>
            <div className="flex flex-col justify-end gap-3 sm:flex-row sm:items-start sm:gap-4">
              <Button
                type="submit"
                variant="brand"
                loading={isPending}
                disabled={isPending}
                className="h-12 px-10! text-base font-semibold uppercase">
                {!isPending && <SendIcon />}
                {isPending ? t('sending') : t('send_message')}
              </Button>
            </div>
          </div>
        </form>
      </Form>
    </div>
  )
}

export default ContactForm
